import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Lock, Video, Calendar, MapPin, Heart, Upload, Shield, CheckSquare, Square, Trash2, Download, LogOut, Share2, X, Mail, Send, ArrowLeft, Eye, EyeOff, Link2, Check } from 'lucide-react';
import JSZip from 'jszip';
import { useEvent } from '../hooks/useEvents';
import { useVideos } from '../hooks/useVideos';
import { deleteVideosAsClient, sendGalleryLinkEmail, sendGalleryLinkEmailAsClient, updateEventPrivacy, verifyClientCredentials } from '../services/emailService';
import { formatDate } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorMessage from '../components/shared/ErrorMessage';
import GalleryVideoCard from '../components/gallery/GalleryVideoCard';
import GuestUploadModal from '../components/gallery/GuestUploadModal';
import stoneGoldBg from '../assets/images/stone-gold-black-bg.png';

function GalleryPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth(); // Check if admin is logged in
  const { event, loading: eventLoading, error: eventError } = useEvent(eventId);
  const { videos: fetchedVideos, loading: videosLoading, error: videosError } = useVideos(eventId);
  const CLIENT_SESSION_TTL_MS = 60 * 60 * 1000; // 1 hour

  const getValidClientSession = () => {
    const sessionKey = `client_auth_${eventId}`;
    const sessionData = sessionStorage.getItem(sessionKey);

    if (!sessionData) return null;

    try {
      const parsed = JSON.parse(sessionData);
      const isSameEvent = parsed?.eventId === eventId;
      const isFresh = typeof parsed?.timestamp === 'number' && (Date.now() - parsed.timestamp) < CLIENT_SESSION_TTL_MS;

      if (isSameEvent && isFresh) {
        return parsed;
      }

      sessionStorage.removeItem(sessionKey);
      return null;
    } catch (err) {
      console.error('Failed to parse client session:', err);
      sessionStorage.removeItem(sessionKey);
      return null;
    }
  };

  // Local state for videos (allows immediate UI updates after deletion)
  const [videos, setVideos] = useState([]);

  // Sync local videos state with fetched videos
  useEffect(() => {
    if (fetchedVideos) {
      setVideos(fetchedVideos);
    }
  }, [fetchedVideos]);

  // Check authentication: Admin (Firebase auth) OR client session
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Admins are always authenticated (bypass password)
    if (currentUser) return true;
    return Boolean(getValidClientSession());
  });

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Client mode state
  const [isClientMode, setIsClientMode] = useState(() => {
    return Boolean(getValidClientSession());
  });
  const [clientPassword, setClientPassword] = useState('');
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmails, setShareEmails] = useState('');
  const [sharePassword, setSharePassword] = useState(''); // For client authentication when sharing
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [shareEmailError, setShareEmailError] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [localIsPublic, setLocalIsPublic] = useState(undefined);
  const [isUpdatingPrivacy, setIsUpdatingPrivacy] = useState(false);
  const [privacyError, setPrivacyError] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyPassword, setPrivacyPassword] = useState('');
  const [isVerifyingPrivateAccess, setIsVerifyingPrivateAccess] = useState(false);
  const isGuestView = !isClientMode && !currentUser;

  // Admins bypass password requirement
  useEffect(() => {
    if (currentUser) {
      setIsAuthenticated(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (event) {
      setLocalIsPublic(event.isPublic ?? true);
    }
  }, [event?.id, event?.isPublic]);

  // Set client password when event loads (if authenticated via session)
  useEffect(() => {
    if (!event || !isAuthenticated || !isClientMode) return;

    const sessionData = getValidClientSession();
    if (sessionData && event.clientPassword) {
      // Store client password for video deletion operations
      setClientPassword(event.clientPassword);
    }
  }, [event, isAuthenticated, isClientMode]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    // Check guest password
    if (event.guestPassword && password === event.guestPassword) {
      setIsAuthenticated(true);
      setIsClientMode(false);
      setPasswordError('');
      return;
    }

    // No password required
    if (!event.guestPassword) {
      setIsAuthenticated(true);
      return;
    }

    setPasswordError('Incorrect password. Please try again.');
    setPassword('');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    const enteredEmail = email.trim();
    const enteredPassword = password.trim();

    if (!enteredEmail) {
      setEmailError('Please enter your email address');
      return;
    }

    if (!enteredPassword) {
      setPasswordError('Please enter your password');
      return;
    }

    setIsVerifyingPrivateAccess(true);
    setEmailError('');
    setPasswordError('');

    const result = await verifyClientCredentials(enteredEmail, enteredPassword);

    if (!result.success) {
      setPasswordError(result.error || 'Invalid email or password. Please try again.');
      setIsVerifyingPrivateAccess(false);
      return;
    }

    const hasAccessToEvent = Array.isArray(result.events)
      && result.events.some(evt => evt.id === eventId);

    if (hasAccessToEvent) {
      setIsAuthenticated(true);
      setEmailError('');
      setPasswordError('');
    } else {
      setPasswordError('This account does not have access to this event.');
    }

    setIsVerifyingPrivateAccess(false);
  };

  const handleToggleSelect = (videoId) => {
    setSelectedVideos(prev => {
      const next = new Set(prev);
      if (next.has(videoId)) {
        next.delete(videoId);
      } else {
        next.add(videoId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedVideos.size === videos.length) {
      setSelectedVideos(new Set());
    } else {
      setSelectedVideos(new Set(videos.map(v => v.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedVideos.size === 0) return;

    const count = selectedVideos.size;
    if (!window.confirm(`Are you sure you want to delete ${count} video${count !== 1 ? 's' : ''}? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    const result = await deleteVideosAsClient(eventId, clientPassword, Array.from(selectedVideos));

    if (result.success) {
      // Remove deleted videos from local state immediately
      setVideos(prevVideos =>
        prevVideos.filter(video => !selectedVideos.has(video.id))
      );
      setSelectedVideos(new Set());
      setIsSelectionMode(false);
    } else {
      setDeleteError(result.error || 'Failed to delete videos.');
    }

    setIsDeleting(false);
  };

  const extensionMap = {
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
    'video/x-msvideo': 'avi',
    'video/x-matroska': 'mkv',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'image/heif': 'heif',
    'image/gif': 'gif'
  };

  const getFilename = (video, mimeType) => {
    const extension = extensionMap[mimeType] || 'bin';
    const isImage = mimeType.startsWith('image/');
    return `${(video.guestName || 'guest').replace(/\s+/g, '_')}_${isImage ? 'photo' : 'video'}.${extension}`;
  };

  const fetchBlob = async (url) => {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  };

  const handleDownload = async (video) => {
    try {
      const blob = await fetchBlob(video.videoUrl);
      const mimeType = blob.type || video.mimeType || '';
      const filename = getFilename(video, mimeType);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in new tab so user can right-click save
      window.open(video.videoUrl, '_blank');
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedVideos.size === 0) return;

    setIsDownloading(true);

    try {
      const zip = new JSZip();
      const selectedList = videos.filter(v => selectedVideos.has(v.id));
      const nameCount = {};

      await Promise.all(selectedList.map(async (video) => {
        try {
          const blob = await fetchBlob(video.videoUrl);
          const mimeType = blob.type || video.mimeType || '';
          let filename = getFilename(video, mimeType);

          // Deduplicate filenames
          if (nameCount[filename]) {
            nameCount[filename]++;
            const dotIndex = filename.lastIndexOf('.');
            filename = `${filename.slice(0, dotIndex)}_${nameCount[filename]}${filename.slice(dotIndex)}`;
          } else {
            nameCount[filename] = 1;
          }

          zip.file(filename, blob);
        } catch (err) {
          console.error(`Failed to fetch ${video.id}:`, err);
        }
      }));

      if (Object.keys(zip.files).length === 0) {
        alert('Failed to download any files. Please check your connection and try again.');
        setIsDownloading(false);
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const eventName = (event?.eventName || 'gallery').replace(/\s+/g, '_');
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${eventName}_media.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Zip download failed:', err);
      alert('Failed to create zip file. Please try again.');
    }

    setIsDownloading(false);
  };

  const handleLogout = () => {
    // Clear authentication state
    setIsAuthenticated(false);
    setIsClientMode(false);
    setClientPassword('');
    setPassword('');
    setSelectedVideos(new Set());
    setIsSelectionMode(false);

    // Clear all client session data
    sessionStorage.removeItem(`client_auth_${eventId}`);
    sessionStorage.removeItem('client_session');

    // Navigate to landing page
    navigate('/');
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setShareEmails('');
    setSharePassword('');
    setShareEmailError('');
    setEmailSuccess(false);
  };

  const handleCopyGalleryLink = async () => {
    const galleryUrl = `${window.location.origin}/gallery/${eventId}`;

    try {
      await navigator.clipboard.writeText(galleryUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      return;
    } catch (err) {
      console.error('Clipboard API copy failed:', err);
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = galleryUrl;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (copied) {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } else {
        alert(`Copy failed. Please copy this link manually:\n${galleryUrl}`);
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed:', fallbackErr);
      alert(`Copy failed. Please copy this link manually:\n${galleryUrl}`);
    }
  };

  const getClientEmail = () => {
    const perEventSession = getValidClientSession();
    if (perEventSession?.email) return perEventSession.email;

    const clientSession = sessionStorage.getItem('client_session');
    if (clientSession) {
      try {
        const sessionData = JSON.parse(clientSession);
        if (sessionData?.email) return sessionData.email;
      } catch (err) {
        console.error('Failed to parse client_session:', err);
      }
    }

    return event?.clientEmail || '';
  };

  const handleClientPrivacyToggle = () => {
    if (!isClientMode || isUpdatingPrivacy || !event) return;
    setPrivacyPassword('');
    setPrivacyError('');
    setShowPrivacyModal(true);
  };

  const handlePrivacyConfirm = async () => {
    if (!privacyPassword.trim()) {
      setPrivacyError('Password is required to update gallery privacy.');
      return;
    }

    const clientEmail = getClientEmail();
    if (!clientEmail) {
      setPrivacyError('Unable to verify client identity. Please log out and log in again.');
      return;
    }

    const currentIsPublic = localIsPublic ?? event.isPublic ?? true;
    const newIsPublic = !currentIsPublic;

    setIsUpdatingPrivacy(true);
    setPrivacyError('');

    const result = await updateEventPrivacy(
      eventId,
      clientEmail,
      privacyPassword.trim(),
      newIsPublic
    );

    if (result.success) {
      setLocalIsPublic(newIsPublic);
      setShowPrivacyModal(false);
      setPrivacyPassword('');
    } else {
      setPrivacyError(result.error || 'Failed to update privacy setting.');
    }

    setIsUpdatingPrivacy(false);
  };

  const handleSendGalleryEmails = async (e) => {
    e.preventDefault();

    if (!shareEmails.trim()) {
      setShareEmailError('Please enter at least one email address');
      return;
    }

    // If client mode, require password for authentication
    if (isClientMode && !sharePassword.trim()) {
      setShareEmailError('Please enter your password to share the gallery');
      return;
    }

    setIsSendingEmails(true);
    setShareEmailError('');
    setEmailSuccess(false);

    let result;

    // Use different functions based on user type
    if (isClientMode) {
      // Client sharing - use client-authenticated function
      // Get client email from session storage or event
      const clientSession = sessionStorage.getItem('client_session');
      let clientEmail = event.clientEmail; // Fallback to event's client email

      if (clientSession) {
        try {
          const sessionData = JSON.parse(clientSession);
          clientEmail = sessionData.email || clientEmail;
        } catch (err) {
          console.error('Failed to parse client session:', err);
        }
      }

      result = await sendGalleryLinkEmailAsClient(
        eventId,
        clientEmail,
        sharePassword,
        shareEmails
      );
    } else {
      // Admin sharing - use admin function (requires Firebase Auth)
      result = await sendGalleryLinkEmail(event, shareEmails);
    }

    if (result.success) {
      setEmailSuccess(true);
      setTimeout(() => {
        handleCloseShareModal();
      }, 2000);
    } else {
      setShareEmailError(result.error || 'Failed to send emails. Please try again.');
    }

    setIsSendingEmails(false);
  };

  if (eventLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-slate">
        <LoadingSpinner message="Loading gallery..." />
      </div>
    );
  }

  if (eventError || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-slate p-6">
        <ErrorMessage message={eventError || 'Event not found'} />
      </div>
    );
  }

  const isPublic = localIsPublic ?? event.isPublic ?? true;

  // Check if authentication is required
  // Note: clientPassword is for client portal access only, not guest gallery access
  const requiresAuth = (!isPublic || event.guestPassword) && !isAuthenticated;

  if (requiresAuth) {
    // Private event - require email verification
    if (!isPublic) {
      return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-dark-slate via-dark-slate to-gold/10">
          {isGuestView && (
            <div className="sticky top-0 z-30 bg-dark-slate/85 backdrop-blur-md border-b border-white/10">
              <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                <Link
                  to="/"
                  className="text-xl font-bold tracking-tight text-white hover:text-gold transition-colors"
                >
                  MomentBox
                </Link>
                <span className="text-sm text-gray-400 hidden sm:inline">Digital Guestbook</span>
              </div>
            </div>
          )}
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-md w-full">
              <div className="glass rounded-2xl p-8">
                {/* Lock Icon */}
                <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-10 h-10 text-blue-500" />
                </div>

                {/* Event Info */}
                <h1 className="text-3xl font-bold text-white text-center mb-2">
                  {event.eventName}
                </h1>
                <p className="text-neutral-grey-300 text-center mb-8">
                  This is a private gallery
                </p>

                {/* Email Form */}
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-white font-medium mb-2">
                      Enter Your Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError('');
                      }}
                      className={`w-full px-4 py-3 bg-white/10 border-2 ${
                        emailError ? 'border-crimson' : 'border-white/20'
                      } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                      placeholder="your.email@example.com"
                      autoFocus
                    />
                    {emailError && (
                      <p className="mt-2 text-crimson text-sm">{emailError}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="privatePassword" className="block text-white font-medium mb-2">
                      Enter Your Password
                    </label>
                    <input
                      type="password"
                      id="privatePassword"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError('');
                      }}
                      className={`w-full px-4 py-3 bg-white/10 border-2 ${
                        passwordError ? 'border-crimson' : 'border-white/20'
                      } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                      placeholder="Enter password"
                    />
                    {passwordError && (
                      <p className="mt-2 text-crimson text-sm">{passwordError}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isVerifyingPrivateAccess}
                    className="w-full btn-primary"
                  >
                    {isVerifyingPrivateAccess ? 'Verifying...' : 'Access Gallery'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Password protected event (guest password only)
    // Note: clientPassword is handled separately via client portal
    if (event.guestPassword) {
      return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-dark-slate via-dark-slate to-gold/10">
          {isGuestView && (
            <div className="sticky top-0 z-30 bg-dark-slate/85 backdrop-blur-md border-b border-white/10">
              <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                <Link
                  to="/"
                  className="text-xl font-bold tracking-tight text-white hover:text-gold transition-colors"
                >
                  MomentBox
                </Link>
                <span className="text-sm text-gray-400 hidden sm:inline">Digital Guestbook</span>
              </div>
            </div>
          )}
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-md w-full">
              <div className="glass rounded-2xl p-8">
                {/* Lock Icon */}
                <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-10 h-10 text-gold" />
                </div>

                {/* Event Info */}
                <h1 className="text-3xl font-bold text-white text-center mb-2">
                  {event.eventName}
                </h1>
                <p className="text-neutral-grey-300 text-center mb-8">
                  This gallery is password protected
                </p>

                {/* Password Form */}
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-white font-medium mb-2">
                      Enter Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError('');
                      }}
                      className={`w-full px-4 py-3 bg-white/10 border-2 ${
                        passwordError ? 'border-crimson' : 'border-white/20'
                      } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                      placeholder="Enter password"
                      autoFocus
                    />
                    {passwordError && (
                      <p className="mt-2 text-crimson text-sm">{passwordError}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary"
                  >
                    View Gallery
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  const eventDate = event.eventDate?.toDate
    ? formatDate(event.eventDate.toDate())
    : formatDate(new Date(event.eventDate));
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-dark-slate via-dark-slate to-gold/10">
      {/* Guest Navbar */}
      {isGuestView && (
        <div className="sticky top-0 z-30 bg-dark-slate/85 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <Link
              to="/"
              className="text-xl font-bold tracking-tight text-white hover:text-gold transition-colors"
            >
              MomentBox
            </Link>
            <span className="text-sm text-gray-400 hidden sm:inline">Digital Guestbook</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className="relative bg-dark-slate/50 backdrop-blur-sm border-b border-white/10 overflow-hidden"
        style={{
          backgroundImage: `url(${stoneGoldBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-slate/85 to-dark-slate/70" />

        {/* Back to Events Button - Only show when in client mode */}
        {isClientMode && (
          <button
            onClick={() => navigate('/client/portal')}
            className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 text-gray-400 hover:text-gold transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Events</span>
          </button>
        )}

        {/* Logout Button - Only show when authenticated (client or admin) - Top Right */}
        {(isAuthenticated || isClientMode) && (
          <button
            onClick={handleLogout}
            className="absolute top-6 right-6 z-20 inline-flex items-center gap-2 text-gray-400 hover:text-gold transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        )}

        <div className="max-w-7xl mx-auto px-6 pt-14 sm:pt-16 pb-6 sm:pb-8 relative z-10">
          <div className="text-center mb-4 sm:mb-6">
            <div className="hidden sm:inline-flex items-center justify-center w-16 h-16 bg-gold rounded-full mb-4 shadow-lg shadow-gold/30">
              <Heart className="w-8 h-8 text-dark-slate" fill="currentColor" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-3">
              {event.eventName}
            </h1>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-sm sm:text-base text-gray-300">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                <span>{eventDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                <span>{event.location}</span>
              </div>
            </div>

            {/* Privacy Info + Copy Link - Only show for client mode */}
            {isClientMode && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                  isPublic
                    ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                    : 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                }`}>
                  {isPublic ? (
                    <>
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">Public Gallery - Anyone with link can view</span>
                      <span className="sm:hidden">Public</span>
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4" />
                      <span className="hidden sm:inline">Private Gallery - Only you and admins can view</span>
                      <span className="sm:hidden">Private</span>
                    </>
                  )}
                </div>
                <button
                  onClick={handleCopyGalleryLink}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    linkCopied
                      ? 'bg-green-500/20 border-green-500/40 text-green-400'
                      : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                  }`}
                >
                  {linkCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                  {linkCopied ? 'Copied' : 'Copy Link'}
                </button>
              </div>
            )}

            {!isClientMode && isPublic && (
              <div className="mt-3 flex justify-center">
                <button
                  onClick={handleCopyGalleryLink}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                    linkCopied
                      ? 'bg-green-500/20 border-green-500/40 text-green-400'
                      : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                  }`}
                >
                  {linkCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                  {linkCopied ? 'Link Copied' : 'Copy Gallery Link'}
                </button>
              </div>
            )}
          </div>

          {/* Stats & Upload Button */}
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div className="inline-flex items-center gap-3 px-5 sm:px-6 py-2.5 sm:py-3 bg-white/10 rounded-full border border-white/20">
              <Video className="w-5 h-5 text-gold" />
              <span className="text-white font-semibold">
                {videos?.length || 0} upload{videos?.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="w-full max-w-md px-6 md:px-8 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all flex items-center justify-center gap-3 shadow-lg shadow-gold/30"
            >
              <Upload className="w-5 h-5" />
              <span className="sm:hidden">Upload</span>
              <span className="hidden sm:inline md:hidden">Share your photos and videos with us</span>
              <span className="hidden md:inline">Share Your Memory</span>
            </button>
          </div>
        </div>
      </div>

      {/* Client Mode Toolbar */}
      {isClientMode && (
        <div className="bg-neutral-900/80 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            {/* Row 1: Client actions + Select/Select All */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold/20 border border-gold/40 text-gold rounded-full text-xs sm:text-sm font-semibold">
                <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Client
              </span>

              <button
                onClick={() => setShowShareModal(true)}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gold/20 hover:bg-gold/30 text-gold font-medium rounded-lg transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm border border-gold/40"
              >
                <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Share
              </button>

              <button
                onClick={handleClientPrivacyToggle}
                disabled={isUpdatingPrivacy}
                className={`px-3 py-1.5 sm:px-4 sm:py-2 font-medium rounded-lg transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm border disabled:opacity-50 disabled:cursor-not-allowed ${
                  isPublic
                    ? 'bg-green-500/20 border-green-500/40 text-green-400 hover:bg-green-500/30'
                    : 'bg-blue-500/20 border-blue-500/40 text-blue-400 hover:bg-blue-500/30'
                }`}
              >
                {isUpdatingPrivacy ? (
                  <>
                    <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span className="hidden sm:inline">Updating...</span>
                  </>
                ) : isPublic ? (
                  <>
                    <EyeOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Make</span> Private
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Make</span> Public
                  </>
                )}
              </button>

              {!isSelectionMode ? (
                <button
                  onClick={() => setIsSelectionMode(true)}
                  className="ml-auto px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                >
                  <CheckSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Select
                </button>
              ) : (
                <button
                  onClick={handleSelectAll}
                  className="ml-auto px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                >
                  {selectedVideos.size === videos?.length ? (
                    <><Square className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Select<span className="hidden sm:inline"> All</span></>
                  ) : (
                    <><CheckSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Select<span className="hidden sm:inline"> All</span></>
                  )}
                </button>
              )}
            </div>

            {/* Row 2: Selection actions (only visible in selection mode) */}
            {isSelectionMode && (
              <div className="flex items-center gap-2 sm:gap-3 mt-2">
                <button
                  onClick={handleDownloadSelected}
                  disabled={selectedVideos.size === 0 || isDownloading}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gold/80 hover:bg-gold text-dark-slate font-medium rounded-lg transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {isDownloading ? 'Zipping...' : `Download${selectedVideos.size > 0 ? ` (${selectedVideos.size})` : ''}`}
                </button>

                <button
                  onClick={handleDeleteSelected}
                  disabled={selectedVideos.size === 0 || isDeleting}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-crimson/80 hover:bg-crimson text-white font-medium rounded-lg transition-all flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {isDeleting ? 'Deleting...' : `Delete${selectedVideos.size > 0 ? ` (${selectedVideos.size})` : ''}`}
                </button>

                <button
                  onClick={() => {
                    setIsSelectionMode(false);
                    setSelectedVideos(new Set());
                  }}
                  className="ml-auto px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all text-xs sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            )}

            {deleteError && (
              <p className="mt-2 text-crimson text-sm">{deleteError}</p>
            )}
          </div>
        </div>
      )}

      {/* Videos Grid - Flex grow to push footer down */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-6 py-12">
        {videosLoading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <LoadingSpinner message="Loading videos..." />
          </div>
        ) : videosError ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <ErrorMessage message={videosError} />
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="w-24 h-24 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl shadow-gold/20 ring-4 ring-gold/10">
              <Video className="w-12 h-12 text-gold" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">
              No uploads yet
            </h2>
            <p className="text-gray-400 text-lg max-w-md">
              Photos and videos will appear here as guests share their messages
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <GalleryVideoCard
                key={video.id}
                video={video}
                isClientMode={isClientMode}
                isSelectionMode={isSelectionMode}
                isSelected={selectedVideos.has(video.id)}
                onToggleSelect={handleToggleSelect}
                onDownload={handleDownload}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer - Pushed to bottom with mt-auto */}
      <div className="mt-auto border-t border-white/10 bg-dark-slate/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-center text-xs">
            <p className="text-gray-400">
              Powered by <span className="text-gold font-semibold">MomentBox</span>
            </p>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <GuestUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        eventId={eventId}
        eventName={event.eventName}
        onUploadSuccess={(newVideo) => {
          // Add new video to local state immediately
          setVideos(prevVideos => [...prevVideos, newVideo]);
        }}
      />

      {/* Share Gallery Modal */}
      {/* Privacy Toggle Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-neutral-900 rounded-2xl max-w-md w-full border border-white/10 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  (localIsPublic ?? event?.isPublic ?? true)
                    ? 'bg-blue-500/20'
                    : 'bg-green-500/20'
                }`}>
                  {(localIsPublic ?? event?.isPublic ?? true) ? (
                    <EyeOff className="w-5 h-5 text-blue-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-green-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {(localIsPublic ?? event?.isPublic ?? true) ? 'Make Gallery Private' : 'Make Gallery Public'}
                  </h2>
                  <p className="text-sm text-gray-400">
                    {(localIsPublic ?? event?.isPublic ?? true)
                      ? 'Only you and admins will be able to view'
                      : 'Anyone with the link will be able to view'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setShowPrivacyModal(false); setPrivacyError(''); }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {privacyError && (
                <div className="bg-crimson/10 border border-crimson text-crimson px-4 py-3 rounded-lg text-sm">
                  {privacyError}
                </div>
              )}

              <div>
                <label htmlFor="privacyPassword" className="block text-white font-medium mb-2">
                  Confirm Your Password
                </label>
                <input
                  type="password"
                  id="privacyPassword"
                  value={privacyPassword}
                  onChange={(e) => { setPrivacyPassword(e.target.value); setPrivacyError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handlePrivacyConfirm(); }}
                  className="w-full px-4 py-3 bg-white/10 border-2 border-white/20 text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400"
                  placeholder="Enter your client password"
                  autoFocus
                />
                <p className="mt-2 text-sm text-gray-400">
                  Your password is required to change gallery visibility
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowPrivacyModal(false); setPrivacyError(''); }}
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePrivacyConfirm}
                  disabled={isUpdatingPrivacy || !privacyPassword.trim()}
                  className="flex-1 px-6 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingPrivacy ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      {(localIsPublic ?? event?.isPublic ?? true) ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                      {(localIsPublic ?? event?.isPublic ?? true) ? 'Make Private' : 'Make Public'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-neutral-900 rounded-2xl max-w-lg w-full border border-white/10 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Share Gallery</h2>
                  <p className="text-sm text-gray-400">Send gallery link to friends & family</p>
                </div>
              </div>
              <button
                onClick={handleCloseShareModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSendGalleryEmails} className="p-6 space-y-4">
              {emailSuccess && (
                <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  <span>Emails sent successfully!</span>
                </div>
              )}

              {shareEmailError && (
                <div className="bg-crimson/10 border border-crimson text-crimson px-4 py-3 rounded-lg">
                  {shareEmailError}
                </div>
              )}

              <div>
                <label htmlFor="shareEmails" className="block text-white font-medium mb-2">
                  Email Addresses
                </label>
                <textarea
                  id="shareEmails"
                  rows="4"
                  value={shareEmails}
                  onChange={(e) => {
                    setShareEmails(e.target.value);
                    setShareEmailError('');
                  }}
                  className="w-full px-4 py-3 bg-white/10 border-2 border-white/20 text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400 resize-none"
                  placeholder="friend@email.com, family@email.com&#10;(Separate multiple emails with commas or new lines)"
                />
                <p className="mt-2 text-sm text-gray-400">
                  {event.guestPassword ? (
                    <>Gallery password will be included in the email</>
                  ) : (
                    <>Recipients will receive a direct link to view the gallery</>
                  )}
                </p>
              </div>

              {/* Password field - only for client mode */}
              {isClientMode && (
                <div>
                  <label htmlFor="sharePassword" className="block text-white font-medium mb-2">
                    Your Password
                  </label>
                  <input
                    type="password"
                    id="sharePassword"
                    value={sharePassword}
                    onChange={(e) => {
                      setSharePassword(e.target.value);
                      setShareEmailError('');
                    }}
                    className="w-full px-4 py-3 bg-white/10 border-2 border-white/20 text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400"
                    placeholder="Enter your password to confirm"
                  />
                  <p className="mt-2 text-sm text-gray-400">
                    🔒 Required to verify your identity before sending emails
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseShareModal}
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSendingEmails}
                  className="flex-1 px-6 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSendingEmails ? (
                    <>
                      <LoadingSpinner size="small" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Emails
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryPage;
